import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Gift, ShoppingBag, Wallet, Package, User } from 'lucide-react';

const nfmt = (v) => (v == null ? null : Number(v).toLocaleString());
const fmtDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const PublicMailerReport = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/mailers/${token}`).then(({ data }) => setData(data)).catch(() => setError(true));
  }, [token]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-400" data-testid="public-mailer-error">this report link is no longer valid</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-400">loading…</div>;

  const { campaign, client, summary, creators } = data;
  const isAffiliate = campaign.type === 'affiliate';
  const isPaid = campaign.type === 'paid';
  const TypeIcon = isAffiliate ? ShoppingBag : isPaid ? Wallet : Gift;
  const dateRange = [fmtDate(campaign.start_date), fmtDate(campaign.end_date)].filter(Boolean).join(' → ');

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-lilac-50 p-6 lg:p-10" data-testid="public-mailer-page">
      <div className="max-w-4xl mx-auto">
        <header className="flex items-center gap-3 mb-6">
          <span className="w-11 h-11 rounded-full bg-white border border-pink-200 flex items-center justify-center text-pink-400 text-lg overflow-hidden">
            {client.logo || <Package className="w-5 h-5" />}
          </span>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">{campaign.name}</h1>
            <p className="text-sm text-slate-500 flex items-center gap-1">{client.name || 'client'} · <TypeIcon className="w-3.5 h-3.5 inline" /> {isAffiliate ? 'tts affiliate' : isPaid ? 'paid campaign' : 'organic mailer'} · view-only</p>
          </div>
        </header>

        <div className="bg-white border border-pink-100 rounded-2xl p-4 mb-6 flex items-center gap-6 flex-wrap text-sm">
          <div data-testid="public-mailer-creator-count"><div className="font-semibold text-slate-900">{summary.total}</div><div className="text-slate-400 text-xs">creators</div></div>
          <div data-testid="public-mailer-ig-followers"><div className="font-semibold text-slate-900">{nfmt(summary.ig_followers) || 0}</div><div className="text-slate-400 text-xs">total ig followers</div></div>
          <div data-testid="public-mailer-tt-followers"><div className="font-semibold text-slate-900">{nfmt(summary.tt_followers) || 0}</div><div className="text-slate-400 text-xs">total tt followers</div></div>
          {dateRange && (
            <div data-testid="public-mailer-campaign-dates"><div className="font-semibold text-slate-900">{dateRange}</div><div className="text-slate-400 text-xs">campaign dates</div></div>
          )}
          {isPaid && (
            <div data-testid="public-mailer-deliverables-total"><div className="font-semibold text-slate-900">{summary.deliverables}</div><div className="text-slate-400 text-xs">deliverables</div></div>
          )}
          {isPaid && (
            <div data-testid="public-mailer-total-cost"><div className="font-semibold text-slate-900">£{nfmt(summary.total_cost) || 0}</div><div className="text-slate-400 text-xs">total cost</div></div>
          )}
        </div>

        {campaign.notes && (
          <div className="bg-lilac-50 border border-lilac-200 rounded-2xl p-4 mb-6 text-sm text-slate-700 italic" data-testid="public-mailer-notes">
            "{campaign.notes}"
          </div>
        )}

        <div className="space-y-3">
          {creators.length === 0 ? (
            <div className="bg-white border border-dashed border-pink-200 rounded-2xl p-10 text-center text-sm text-slate-400">no creators yet</div>
          ) : creators.map((c) => (
            <div key={c.id} className="bg-white border border-pink-100 rounded-2xl p-4 flex items-center gap-3" data-testid={`public-mailer-creator-${c.id}`}>
              <span className="shrink-0 w-11 h-11 rounded-full overflow-hidden bg-pink-50 border border-pink-100 flex items-center justify-center">
                {c.photo_url ? <img src={c.photo_url} alt="" className="w-full h-full object-cover" /> : <User className="w-4 h-4 text-pink-300" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-semibold text-slate-800 text-base">{c.name}</span>
                  {!isAffiliate && c.ig_link && (
                    <a href={c.ig_link} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-500 hover:text-pink-600 underline underline-offset-2">
                      {c.ig_followers != null ? `${nfmt(c.ig_followers)} ig followers` : 'instagram'}
                    </a>
                  )}
                  {c.tt_link && (
                    <a href={c.tt_link} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-500 hover:text-pink-600 underline underline-offset-2">
                      {c.tt_followers != null ? `${nfmt(c.tt_followers)} tt followers` : 'tiktok'}
                    </a>
                  )}
                  {isAffiliate && c.level != null && <span className="text-xs text-slate-500">level {c.level}</span>}
                  {isAffiliate && c.post_rate != null && <span className="text-xs text-slate-500">{c.post_rate}% rate</span>}
                  {isAffiliate && c.gmv_last_30_days != null && <span className="text-xs text-slate-500">£{nfmt(c.gmv_last_30_days)} gmv (30d)</span>}
                  {!isAffiliate && c.affiliate_code && <span className="text-xs text-slate-500">code: {c.affiliate_code}</span>}
                  {isPaid && c.rate != null && <span className="text-xs text-slate-500">£{nfmt(c.rate)} rate</span>}
                  {isPaid && c.deliverables && <span className="text-xs text-slate-500">{c.deliverables}</span>}
                  {isPaid && c.usage && <span className="text-xs text-slate-500">usage: {c.usage}</span>}
                </div>

                {isPaid && c.boosting_code && (
                  <div className="mt-2 pt-2 border-t border-pink-100 flex items-center gap-4 flex-wrap text-xs text-slate-500">
                    <span data-testid={`public-mailer-boosting-${c.id}`}>boost code: {c.boosting_code}</span>
                  </div>
                )}

                {isPaid && c.deliverable_items?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-pink-100 space-y-1.5">
                    {c.deliverable_items.map((d, i) => (
                      <div key={i} className="flex items-center gap-3 text-xs text-slate-600 flex-wrap" data-testid={`public-mailer-deliverable-${c.id}-${i}`}>
                        <span className="font-medium text-slate-700">{d.name || `deliverable ${i + 1}`}</span>
                        {d.due_date && <span>due {fmtDate(d.due_date)}</span>}
                        {d.live_date && <span>live {fmtDate(d.live_date)}</span>}
                        {d.boosting_code_ig && <span>ig boost: {d.boosting_code_ig}</span>}
                        {d.boosting_code_tt && <span>tt boost: {d.boosting_code_tt}</span>}
                        {d.brief_link && (
                          <a href={d.brief_link} target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-700 underline underline-offset-2">brief</a>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {(c.ig_content_links.length > 0 || c.tt_content_links.length > 0) && (
                  <div className="mt-3 pt-3 border-t border-pink-100 space-y-1.5">
                    {c.ig_content_links.map((l, i) => (
                      <div key={`ig-${i}`} className="flex items-center gap-3 text-xs text-slate-600" data-testid={`public-mailer-ig-link-${c.id}-${i}`}>
                        <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-700 truncate max-w-[220px]">{l.url}</a>
                        {l.likes != null && <span>{nfmt(l.likes)} likes</span>}
                        {l.views != null && <span>{nfmt(l.views)} views</span>}
                        {l.notes && <span className="italic text-slate-400 truncate max-w-[160px]">"{l.notes}"</span>}
                      </div>
                    ))}
                    {c.tt_content_links.map((l, i) => (
                      <div key={`tt-${i}`} className="flex items-center gap-3 text-xs text-slate-600" data-testid={`public-mailer-tt-link-${c.id}-${i}`}>
                        <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-700 truncate max-w-[220px]">{l.url}</a>
                        {l.likes != null && <span>{nfmt(l.likes)} likes</span>}
                        {l.views != null && <span>{nfmt(l.views)} views</span>}
                        {isAffiliate && l.gmv != null && <span>£{nfmt(l.gmv)} gmv</span>}
                        {l.notes && <span className="italic text-slate-400 truncate max-w-[160px]">"{l.notes}"</span>}
                      </div>
                    ))}
                  </div>
                )}

                {c.screenshots?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-pink-100 flex items-center gap-2 flex-wrap">
                    {c.screenshots.map((url) => (
                      <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="w-14 h-14 rounded-md overflow-hidden ring-1 ring-slate-200 block">
                        <img src={url} alt="" className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <p className="text-center text-[11px] text-slate-400 mt-8">shared read-only mailer report · lve social</p>
      </div>
    </div>
  );
};

export default PublicMailerReport;
